import React from "react";
import { HashRouter as Router } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material";
import { UseCasesProvider, type UseCases } from "@gui/contexts/useCases/UseCasesContext";
import { authUseCase, passwordResetUseCase } from "../composition/auth";
import { companiesUseCase } from "../composition/companies";
import { submitJobsUseCase, missionsUseCase } from "../composition/jobs";
import { UserContext } from "../../../hooks/UserContext";
import { CompanyContextProvider } from "../../../hooks/CompanyContext";
import { IssueCountsProvider } from "../../../hooks/IssueCountsContext";
import { User } from "../../../models";

const useCases: UseCases = {
	auth: authUseCase,
	passwordReset: passwordResetUseCase,
	companies: companiesUseCase,
	submitJobs: submitJobsUseCase,
	missions: missionsUseCase,
};

export const theme = createTheme({
	palette: {
		primary: {
			main: "#274052",
		},
		secondary: {
			main: "#dc004e",
		},
	},
});

interface AppProvidersProps {
	currentUser: User | undefined;
	children: React.ReactNode;
}

/**
 * Composition root for the app's React provider tree.
 *
 * Order is significant (outermost → innermost):
 * - UseCasesProvider: outermost so any consumer (including auth flows that run before a user
 *   exists) can invoke use cases without depending on UserContext.
 * - UserContext: above ThemeProvider so any consumer can read currentUser without a CompanyContext.
 * - ThemeProvider: wraps everything that uses MUI styling.
 * - Router: wraps everything that uses routing (must be inside ThemeProvider since some MUI
 *   components rely on the theme inside route elements).
 * - CompanyContextProvider: depends on currentUser and is consumed by feature pages, so it
 *   sits inside Router and UserContext.
 * - IssueCountsProvider: scoped to authenticated feature pages and may depend on the active
 *   company, so it sits innermost around children.
 */
export function AppProviders({ currentUser, children }: AppProvidersProps) {
	return (
		<UseCasesProvider value={useCases}>
			<UserContext.Provider value={currentUser}>
				<ThemeProvider theme={theme}>
					<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
						<CompanyContextProvider user={currentUser}>
							<IssueCountsProvider>{children}</IssueCountsProvider>
						</CompanyContextProvider>
					</Router>
				</ThemeProvider>
			</UserContext.Provider>
		</UseCasesProvider>
	);
}
